import React from "react";
import {
  Card,
  Typography,
  Chip,
  Stack,
  Collapse,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box } from "@mui/system";
import {
  typography,
  spacing,
  colors,
  shadows,
} from "../../../../../../../../../assets/styles/tokens";
import KeywordsSearchItemsAdsInfo from "./KeywordsSearchItemsAdsInfo";


type StatusColor = "success" | "warning" | "error";

interface KeywordsSearchItemsCardProps {
  title: string;
  brandIcon: string | null;
  cost: string;
  impPercentage: string;
  impColor: StatusColor;
  ftd: string;
  cac: string;
  cvrPercentage: string;
  cvrColor: StatusColor;
  tier: string;
  adGroup: string;
  campaign: string;
}

const buildShadow = (shadowToken: any) =>
  shadowToken.$value
    .map(
      (s: any) => `${s.offsetX} ${s.offsetY} ${s.blur} ${s.spread} ${s.color}`,
    )
    .join(", ");

export function KeywordsSearchItemsCard({
  title,
  brandIcon,
  cost,
  impPercentage,
  impColor,
  ftd,
  cac,
  cvrPercentage,
  cvrColor,
  tier,
  adGroup,
  campaign,
}: KeywordsSearchItemsCardProps) {
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <Card
        sx={{
          p: 1,
          borderRadius: spacing.Radius.lg.$value,
          boxShadow: buildShadow(shadows.Shadows.sm),
          border: "1px solid" + colors.GrayLightMode[200].$value,
          mb: spacing.Spacing.xs.$value + "!important",
          "&:not(:first-of-type)": {
            mt: spacing.Spacing.lg.$value,
          },
        }}
      >
        {/* Top Row */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={0.5}
        >
          <Typography
            variant="body1"
            sx={{
              color: colors.GrayLightMode[700].$value,
              fontWeight: typography.Font_weight.Medium.$value,
              fontSize: typography.Size.Text.xs.$value,
              m: 0,
            }}
          >
            {title}
          </Typography>

          {brandIcon && <img src={brandIcon} alt="Ad platform" width={20} />}
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: spacing.Spacing.xs.$value }}
        >
          <Typography
            variant="body1"
            sx={{ fontSize: typography.Size.Text.sm.$value }}
          >
            Cost
            <Box
              component="span"
              sx={{
                fontSize: typography.Size.Text.xl.$value,
                fontWeight: typography.Font_weight.Medium.$value,
                ml: spacing.Spacing.xs.$value,
              }}
            >
              {cost}
            </Box>
          </Typography>

          <Box display={"flex"} alignItems="center">
            <Typography
              variant="body2"
              sx={{
                color: colors.GrayLightMode[900].$value,
                mr: 1,
              }}
            >
              IMP Share
            </Typography>
            <Chip
              label={impPercentage}
              size="small"
              sx={{
                borderRadius: spacing.Radius.xs.$value,
                bgcolor:
                  impColor === "success"
                    ? colors.Success[50].$value
                    : impColor === "warning"
                      ? colors.Warning[50].$value
                      : colors.Error[50].$value,

                color:
                  impColor === "success"
                    ? colors.Success[600].$value
                    : impColor === "warning"
                      ? colors.Warning[600].$value
                      : colors.Error[600].$value,

                fontWeight: typography.Font_weight.Semibold.$value,
              }}
            />
          </Box>
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box display={"flex"} alignItems="center">
            <Typography
              variant="body2"
              sx={{
                color: colors.GrayLightMode[900].$value,
              }}
            >
              <Box
                component="span"
                sx={{
                  fontSize: typography.Size.Text.sm.$value,
                  fontWeight: typography.Font_weight.Medium.$value,
                  mr: spacing.Spacing.xs.$value,
                }}
              >
                {ftd}
              </Box>
              FTD’s at
              <Box
                component="span"
                sx={{
                  fontSize: typography.Size.Text.sm.$value,
                  fontWeight: typography.Font_weight.Medium.$value,
                  mx: spacing.Spacing.xs.$value,
                }}
              >
                {cac}
              </Box>
              CAC
            </Typography>
          </Box>
          <Box display={"flex"} alignItems="center">
            <Typography
              variant="body2"
              sx={{
                color: colors.GrayLightMode[900].$value,
                mr: 1,
              }}
            >
              CVR
            </Typography>
            <Chip
              label={cvrPercentage}
              size="small"
              sx={{
                borderRadius: spacing.Radius.xs.$value,
                bgcolor:
                  cvrColor === "success"
                    ? colors.Success[50].$value
                    : cvrColor === "warning"
                      ? colors.Warning[50].$value
                      : colors.Error[50].$value,

                color:
                  cvrColor === "success"
                    ? colors.Success[600].$value
                    : cvrColor === "warning"
                      ? colors.Warning[600].$value
                      : colors.Error[600].$value,

                fontWeight: typography.Font_weight.Semibold.$value,
              }}
            />
          </Box>
        </Stack>

        {/* Predicted Row */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mt={spacing.Spacing.xxs.$value}
        >
          <Box display={"flex"} alignItems="center">
            <Typography
              variant="body2"
              sx={{
                color: colors.GrayLightMode[900].$value,
              }}
            >
              Tier
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: colors.GrayLightMode[900].$value,
                fontWeight: typography.Font_weight.Bold.$value,
                mx: 0.5,
              }}
            >
              {tier}
            </Typography>
          </Box>

          <Button
            size="small"
            onClick={() => setOpen(!open)}
            sx={{
              padding: "0",
              fontSize: typography.Size.Text.xs.$value,
              color: colors.GrayLightMode[900].$value,
              textTransform:"capitalize",
              fontWeight:typography.Font_weight.Regular.$value,
              letterSpacing:'0px',
            }}
             data-tracker="667"
          >
            {open ? "Less Ads Info" : "Ads Info"}
            <ExpandMoreIcon
              sx={{
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
                transition: "0.2s",
              }}
            />
          </Button>
        </Stack>

        {/* Expandable Info */}
        <Collapse in={open}>
          <KeywordsSearchItemsAdsInfo adGroup={adGroup} campaign={campaign} />
        </Collapse>
      </Card>
    </>
  );
}

export default KeywordsSearchItemsCard;
